/* global $3Dmol */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Space,
  Button,
  Typography,
  message,
  Checkbox,
  Divider,
  Collapse,
  Radio,
  Spin,
  Alert,
  Layout,
  Row,
  Col,
  Tooltip,
} from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { postRequest } from "../../util/service";
import { ArrowLeftOutlined, RedoOutlined } from "@ant-design/icons";
import BreadcrumbTrail from "../shared/BreadcrumbTrail";

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Sider, Content } = Layout;

const cloneCameraView = (view) => {
  if (!view) return null;
  if (Array.isArray(view)) return [...view];
  if (typeof view.slice === "function") return view.slice();
  return view;
};

export default function Glide3DPage() {
  const navigate = useNavigate();
  const { projectId, batchId } = useParams();
  const { state } = useLocation();
  const { proteinPdbFile, ligandPdbFile, glideId, phaseId } = state || {};

  const [proteinPdb, setProteinPdb] = useState("");
  const [ligandPdb, setLigandPdb] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // --- State for Protein Controls ---
  const [proteinBaseStyles, setProteinBaseStyles] = useState(["cartoon"]);
  const [proteinColorScheme, setProteinColorScheme] = useState("chain");
  const [showProteinSurface, setShowProteinSurface] = useState(false);

  // --- State for Ligand Controls ---
  const [ligandBaseStyles, setLigandBaseStyles] = useState(["stick"]);
  const [ligandColorScheme, setLigandColorScheme] = useState("Jmol");
  const [showLigandSurface, setShowLigandSurface] = useState(false);

  // --- Global Overlays ---
  const [showLabels, setShowLabels] = useState(false);
  const [showHBonds, setShowHBonds] = useState(false);

  const viewerDiv = useRef(null);
  const viewer = useRef(null);
  const ligandModelRef = useRef(null);
  const initialViewRef = useRef(null);

  const fetchPdbFromUrl = useCallback(
    async (pdbFile) => {
      if (!pdbFile) return;

      try {
        const usecase = glideId ? "glide" : "phase";
        const usecaseId = glideId || phaseId;

        if (!usecaseId) {
          throw new Error("No Usecase ID (Glide or Phase) was provided.");
        }

        const payload = {
          projectId,
          batchId,
          usecase: usecase,
          usecaseId: usecaseId,
          pdbFile,
        };
        const urlResponse = await postRequest("/riva/sd/pdb", payload);
        if (!urlResponse || !urlResponse.data || !urlResponse.data.url)
          throw new Error(`Could not get URL for ${pdbFile}`);

        const pdbResponse = await fetch(urlResponse.data.url);
        if (!pdbResponse.ok)
          throw new Error(`Failed to download PDB from S3 for ${pdbFile}`);
        return await pdbResponse.text();
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    [projectId, batchId, glideId, phaseId]
  );

  useEffect(() => {
    if (!ligandPdbFile) {
      setError("Ligand file information is missing.");
      setLoading(false);
      return;
    }
    const loadAllData = async () => {
      setLoading(true);
      setError("");
      try {
        const results = await Promise.allSettled([
          fetchPdbFromUrl(proteinPdbFile),
          fetchPdbFromUrl(ligandPdbFile),
        ]);

        const proteinResult = results[0];
        const ligandResult = results[1];

        if (proteinResult.status === "fulfilled") {
          setProteinPdb(proteinResult.value);
        } else if (proteinResult.status === "rejected" && proteinPdbFile) {
          throw proteinResult.reason;
        }

        if (ligandResult.status === "fulfilled") {
          setLigandPdb(ligandResult.value);
        } else {
          throw ligandResult.reason;
        }
      } catch (err) {
        const errorMessage =
          err.message || "An error occurred while loading data.";
        setError(errorMessage);
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    loadAllData();
  }, [proteinPdbFile, ligandPdbFile, fetchPdbFromUrl]);

  useEffect(() => {
    if (viewerDiv.current && window.$3Dmol && !viewer.current) {
      viewer.current = window.$3Dmol.createViewer(viewerDiv.current, {
        backgroundColor: "white",
      });
      viewer.current.render();
    }
  }, []);

  useEffect(() => {
    initialViewRef.current = null;
  }, [proteinPdb, ligandPdb]);

  const setInitialCameraView = useCallback((v) => {
    if (!v) return;
    v.zoomTo();
    v.render();
    if (typeof v.getView === "function") {
      initialViewRef.current = cloneCameraView(v.getView());
    }
  }, []);

  const handleResetView = useCallback(() => {
    const v = viewer.current;
    if (!v) return;

    if (initialViewRef.current && typeof v.setView === "function") {
      v.setView(cloneCameraView(initialViewRef.current));
    } else {
      setInitialCameraView(v);
      return;
    }
    v.render();
  }, [setInitialCameraView]);

  useEffect(() => {
    const v = viewer.current;
    if (!v || loading) return;

    v.clear();
    ligandModelRef.current = null;

    if (!proteinPdb && !ligandPdb) {
      v.render();
      return;
    }

    try {
      let proteinModel;
      if (proteinPdb) proteinModel = v.addModel(proteinPdb, "pdb");
      if (ligandPdb) ligandModelRef.current = v.addModel(ligandPdb, "pdb");

      // --- Apply Protein Styles ---
      if (proteinModel) {
        const proteinSelector = { model: proteinModel };
        // Always clear all styles first
        v.setStyle(proteinSelector, {});
        // Then apply only the selected styles
        if (proteinBaseStyles.length > 0) {
          proteinBaseStyles.forEach((styleKey) => {
            v.addStyle(proteinSelector, {
              [styleKey]: { colorscheme: proteinColorScheme },
            });
          });
        }
        if (showProteinSurface) {
          v.addSurface(
            $3Dmol.SurfaceType.VDW,
            { opacity: 0.85, colorscheme: proteinColorScheme },
            proteinSelector
          );
        }
      }

      // --- Apply Ligand Styles ---
      if (ligandModelRef.current) {
        const ligandSelector = { model: ligandModelRef.current };
        // Always clear all styles first
        v.setStyle(ligandSelector, {});
        // Then apply only the selected styles
        if (ligandBaseStyles.length > 0) {
          ligandBaseStyles.forEach((styleKey) => {
            v.addStyle(ligandSelector, {
              [styleKey]: { colorscheme: ligandColorScheme },
            });
          });
        }
        if (showLigandSurface) {
          v.addSurface(
            $3Dmol.SurfaceType.VDW,
            { opacity: 0.85, colorscheme: ligandColorScheme },
            ligandSelector
          );
        }
      }

      if (showHBonds && proteinModel && ligandModelRef.current) {
        try {
          const proteinAtoms = proteinModel.atoms || [];
          const ligandAtoms = ligandModelRef.current.atoms || [];
          
          console.log("H-bonds: protein atoms:", proteinAtoms.length, "ligand atoms:", ligandAtoms.length);
          
          if (proteinAtoms.length === 0 || ligandAtoms.length === 0) {
            message.warning("No atom data found. Models may not have loaded properly.");
            return;
          }
          
          const hbondColor = 0x0000FF; 
          const maxDistance = 3.5;
          let hbondCount = 0;
          
          proteinAtoms.forEach((pAtom) => {
            if (pAtom.x === undefined || pAtom.y === undefined || pAtom.z === undefined) return;
            
            ligandAtoms.forEach((lAtom) => {
              if (lAtom.x === undefined || lAtom.y === undefined || lAtom.z === undefined) return;
              
              const dx = pAtom.x - lAtom.x;
              const dy = pAtom.y - lAtom.y;
              const dz = pAtom.z - lAtom.z;
              const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
              
              if (distance < maxDistance && distance > 1.5) {
                v.addLine({
                  start: { x: pAtom.x, y: pAtom.y, z: pAtom.z },
                  end: { x: lAtom.x, y: lAtom.y, z: lAtom.z },
                  color: hbondColor,
                  linewidth: 2
                });
                hbondCount++;
              }
            });
          });
          
          v.render();
          console.log("Total H-bonds found:", hbondCount);
          
          if (hbondCount > 0) {
            message.success(`Found ${hbondCount} potential hydrogen bonds`);
          } else {
            message.info("No hydrogen bonds detected at typical distances (1.5-3.5 Å)");
          }
        } catch (hbErr) {
          console.error("Error visualizing hydrogen bonds:", hbErr);
          console.error("proteinModel:", proteinModel);
          console.error("ligandModel:", ligandModelRef.current);
          message.warning("Could not visualize hydrogen bonds: " + hbErr.message);
        }
      }
      if (showLabels)
        v.addResLabels(
          {},
          {
            showResidue: "resi",
            fontColor: "black",
            backgroundColor: "lightgray",
            backgroundOpacity: 0.7,
          }
        );

      if (!initialViewRef.current) {
        setInitialCameraView(v);
      } else {
        v.render();
      }
    } catch (err) {
      console.error("3Dmol error:", err);
      message.error("Could not render the PDB files.");
    }
  }, [
    proteinPdb,
    ligandPdb,
    proteinBaseStyles,
    proteinColorScheme,
    showProteinSurface,
    ligandBaseStyles,
    ligandColorScheme,
    showLigandSurface,
    showLabels,
    showHBonds,
    loading,
    setInitialCameraView,
  ]);

  return (
    <div style={{ padding: 24, background: "#f0f2f5", minHeight: "100vh" }}>
      <Space style={{ marginBottom: 16, marginTop: 70 }} align="center">
        <Tooltip title="Back to Results">
                                  <Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />} />
                               </Tooltip>
        <Title level={3} style={{ margin: 0 }}>
          3D Glide Molecule Viewer
        </Title>
      </Space>
      <BreadcrumbTrail />

      <Layout style={{ background: "transparent" }}>
        <Sider
          theme="light"
          width={300}
          style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            marginRight: "16px",
            height: "calc(100vh - 160px)",
            overflowY: "auto",
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Controls
              </Title>
            </Col>
            <Col>
              <Tooltip title="Reset Camera View">
                <Button icon={<RedoOutlined />} onClick={handleResetView} />
              </Tooltip>
            </Col>
          </Row>
          <Collapse
            defaultActiveKey={["protein", "ligand", "overlays"]}
            bordered={false}
            style={{ background: "transparent" }}
          >
            <Panel
              header={<Text strong>Protein Controls</Text>}
              key="protein"
              style={{ border: "none" }}
              disabled={!proteinPdb}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Representation</Text>
                <Checkbox.Group
                  options={[
                    { label: "Cartoon", value: "cartoon" },
                    { label: "Stick", value: "stick" },
                    { label: "Line", value: "line" },
                  ]}
                  value={proteinBaseStyles}
                  onChange={setProteinBaseStyles}
                />
                <Divider style={{ margin: "12px 0" }} />
                <Text strong>Color Scheme</Text>
                <Radio.Group
                  onChange={(e) => setProteinColorScheme(e.target.value)}
                  value={proteinColorScheme}
                >
                  <Space direction="vertical">
                    <Radio value="chain">By Chain</Radio>
                    <Radio value="spectrum">Spectrum</Radio>
                    <Radio value="ssPyMol">Secondary Structure</Radio>
                    <Radio value="Jmol">Element</Radio>
                  </Space>
                </Radio.Group>
                <Divider style={{ margin: "12px 0" }} />
                <Checkbox
                  checked={showProteinSurface}
                  onChange={(e) => setShowProteinSurface(e.target.checked)}
                >
                  Show Surface
                </Checkbox>
              </Space>
            </Panel>
            <Panel
              header={<Text strong>Ligand Controls</Text>}
              key="ligand"
              style={{ border: "none" }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Representation</Text>
                <Checkbox.Group
                  options={[
                    { label: "Stick", value: "stick" },
                    { label: "Line", value: "line" },
                    { label: "Sphere", value: "sphere" },
                  ]}
                  value={ligandBaseStyles}
                  onChange={setLigandBaseStyles}
                />
                <Divider style={{ margin: "12px 0" }} />
                <Text strong>Color Scheme</Text>
                <Radio.Group
                  onChange={(e) => setLigandColorScheme(e.target.value)}
                  value={ligandColorScheme}
                >
                  <Space direction="vertical">
                    <Radio value="Jmol">Element</Radio>
                    <Radio value="chain">By Chain</Radio>
                  </Space>
                </Radio.Group>
                <Divider style={{ margin: "12px 0" }} />
                <Checkbox
                  checked={showLigandSurface}
                  onChange={(e) => setShowLigandSurface(e.target.checked)}
                >
                  Show Surface
                </Checkbox>
              </Space>
            </Panel>
            <Panel
              header={<Text strong>Global Overlays</Text>}
              key="overlays"
              style={{ border: "none" }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Checkbox
                  checked={showHBonds}
                  onChange={(e) => setShowHBonds(e.target.checked)}
                  disabled={!proteinPdb}
                >
                  Show Hydrogen Bonds
                </Checkbox>
                <Checkbox
                  checked={showLabels}
                  onChange={(e) => setShowLabels(e.target.checked)}
                >
                  Show Residue Labels
                </Checkbox>
              </Space>
            </Panel>
          </Collapse>
        </Sider>
        <Content>
          <div
            ref={viewerDiv}
            style={{
              width: "100%",
              height: "75vh",
              border: "1px solid #d9d9d9",
              borderRadius: "8px",
              background: "#fafafa",
              position: "relative",
            }}
          >
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  zIndex: 10,
                }}
              >
                <Spin size="large" />
                <Text style={{ marginTop: 16 }}>Loading 3D Structures...</Text>
              </div>
            )}
            {error && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  padding: 20,
                }}
              >
                <Alert
                  message="Error Loading Data"
                  description={error}
                  type="error"
                  showIcon
                />
              </div>
            )}
          </div>
        </Content>
      </Layout>
    </div>
  );
}
